import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UserPlus } from 'lucide-react';
import {
    useCreate,
    useDataProvider,
    useNotify,
    useRecordContext,
    useRedirect
} from 'ra-core';
import { useState } from 'react';
import type { Contact } from '../types';

export const ConvertLeadToEnquiryButton = () => {
    const record = useRecordContext<Contact>();
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const [score, setScore] = useState(50);
    const [loading, setLoading] = useState(false);
    const [create] = useCreate();
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    if (!record) return null;

    const handleConvert = async () => {
        setLoading(true);
        try {
            // 1. Create the Enquiry
            const enquiryData = {
                contact_id: record.id,
                company_id: record.company_id,
                reference: `ENQ-${record.last_name?.toUpperCase() || 'GSW'}-${Math.floor(Math.random() * 1000)}`,
                status: 'qualified',
                qualification_notes: notes,
                qualification_score: score,
                qualified_at: new Date().toISOString(),
                source: 'Direct/Lead Conversion',
                assigned_to: record.sales_id
            };

            create(
                'enquiries',
                { data: enquiryData },
                {
                    onSuccess: async (newRecord: any) => {
                        // 2. Update Contact Status (Optional but helpful for visual flow)
                        await dataProvider.update('contacts', {
                            id: record.id,
                            data: { 
                                status: 'hot', 
                                background: (record.background || '') + `\n\nConverted to Enquiry on ${new Date().toLocaleDateString()}: ${notes}` 
                            },
                            previousData: record
                        });

                        notify('Contact converted to Enquiry successfully', { type: 'success' });
                        setOpen(false);
                        redirect('show', 'enquiries', newRecord.id);
                    },
                    onError: (error: any) => {
                        notify(`Error converting lead: ${error.message}`, { type: 'error' });
                    }
                }
            );
        } catch (error: any) {
            notify(`Error: ${error.message}`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                    <UserPlus className="w-4 h-4 text-blue-500" />
                    Convert to Enquiry
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Qualify Lead</DialogTitle>
                    <DialogDescription>
                        Assess this contact before promoting it to a formal sales enquiry.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                        <Label htmlFor="score">Qualification Score (0-100)</Label>
                        <Input
                            id="score"
                            type="number"
                            min="0"
                            max="100"
                            value={score}
                            onChange={(e) => setScore(parseInt(e.target.value))}
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="notes">Qualification Notes</Label>
                        <Textarea
                            id="notes"
                            placeholder="Why is this lead qualified? What are their key requirements?"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleConvert} disabled={loading || !notes}>
                        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Convert Now
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
